<template xmlns="http://www.w3.org/1999/html">
    <div>
        <v-app id="inspire" :class="[{activeRtl:$store.state.rtl}, $store.state.appClass]">
            <div v-if="signupEnabled || user">
                <v-navigation-drawer class="navigation-user" v-if="user && !isMobileLobby"
                                     v-model="$store.state.sidebar.user"
                                     :right="!$store.state.rtl" temporary fixed>
                    <div id="userSidebar">
                        <the-user-sidebar v-if="userSidebarState"/>
                    </div>
                </v-navigation-drawer>

                <v-navigation-drawer class="navigation-menu" v-model="$store.state.sidebar.main"
                                     :right="$store.state.rtl"
                                     temporary fixed v-if="!isMobileLobby">
                    <the-main-sidebar v-if="mainSidebarState"/>
                </v-navigation-drawer>

                <v-app-bar app v-if="!isMobileLobby">
                    <v-app-bar-nav-icon class="btn-menu" @click.stop="switchMainSidebar"/>
                    <the-main-header/>
                </v-app-bar>

                <v-main>
                    <v-container :class="$route.params.slug ? $route.params.slug : 'main'" id="cont" fluid>
                        <transition name="fade" mode="out-in">
                            <router-view class="view"></router-view>
                        </transition>
                    </v-container>
                </v-main>

                <game-mobile-menu v-if="isMobileLobby"/>

                <the-footer v-if="!isLobby"/>

                <pwa-popup/>
            </div>

            <div class="app-simple-login" v-else>
                <v-dialog scrollable persistent
                          content-class="dialog-login" max-width="472px"
                          v-model="!$store.state.signIn.forgotPasswordDialog">
                    <login @openForgot='openForgotDialog'/>
                </v-dialog>
                <v-dialog scrollable persistent
                          content-class="dialog-forgot" max-width="472px"
                          v-model="$store.state.signIn.forgotPasswordDialog">
                    <forgot-password @openLogin='openLoginDialog'/>
                </v-dialog>
            </div>
        </v-app>

        <game-tray :ref="'gameTrey/' + launchedGame.alias" :index="index"
                   :launchedGame="launchedGame"
                   v-for="(launchedGame, index) in launchedGames" :key="launchedGame.alias"
                   v-if="launchedGames.length"/>
    </div>
</template>

<script>
import appConfig, {brandName} from "../config/app";
import cookieNames from "./interfaces/cookieNamesInterface";
import {luckWheelEnabled} from "../config/app";
import apiData from "./mixins/apiData";
import toaster from "./mixins/toaster";
import hashRoute from "./mixins/hashRoute";
import {isDeviceMobile, getDeviceOrientation} from "./util/screen/screenResolution";
import {setGuestToken} from "./util/user/setGuestData";
import eventBus from './vue/eventBus';
import {hashRoutes} from "../config/url";
import {routesNames} from "./interfaces/routeInterface";

export default {
    name: "AppContent",

    mixins: [apiData, toaster, hashRoute],

    metaInfo() {
        let pageData = this.$store.getters.getPageData(this.$route);

        if (pageData && pageData.page && pageData.page.metaInfo){
            return pageData.page.metaInfo;
        }else {
            return {
                htmlAttrs: {
                    lang: this.locale,
                },
                link: [
                    {rel: 'shortcut icon', href: `/public/${brandName}/logotype/shortcut-icon.png`},
                    {rel: 'apple-touch-icon', href: `/public/${brandName}/logotype/apple-touch-icon.png`},
                ]
            };
        }
    },

    created() {
        this.$vuetify.rtl = this.$store.state.rtl;
        !this.signupEnabled && this.$store.commit('OPEN_LOGIN_DIALOG');
    },

    mounted() {
        this.defineScreenResolution();
        this.defineOrientation();

        this.user && this.setUser();

        // for browser 'back' button (no request to node but sessionKey presents)
        if (this.user === undefined && this.$cookies.isKey(cookieNames.sessionKey)) {
            this.$store.dispatch('FETCH_USER', this.createApiData({
                sessionKey: this.$cookies.get(cookieNames.sessionKey),
            }))
        }

        this.initChat(true);

        eventBus.bus.$on(eventBus.events.backendApiThrottle, () => this.makeErrorToast(this.$t('front.api.too_many_requests')));
        eventBus.bus.$on(eventBus.events.invalidUserSession, () => this.userSessionInvalid());

        window.addEventListener('resize', this.resize);
        window.addEventListener('orientationchange', this.orientationchange);

        // Important! All components (dialogs) will be mounted before can handle hash routes
        this.hashRoute && setTimeout(() => this.handleHashRoute(this.hashRoute, this.user), 2000);

        luckWheelEnabled && setTimeout(this.fetchFortuneWheels, this.isFortuneWheelRoute ? 0 : 2000);
    },

    beforeDestroy() {
        window.removeEventListener('resize', this.resize);
        window.removeEventListener('orientationchange', this.orientationchange);
    },

    watch: {
        user(user, oldUser) {
            if (user && !oldUser) {
                this.setUser();
                this.initChat();
                this.loadUserTranslationMessages();
            }

            if(user && !oldUser || !user && oldUser){
                this.$store.dispatch('FETCH_LAYOUT_PARTS', this.createApiData({
                    mustReload: true,
                }));

                luckWheelEnabled && this.resetFortuneWheel();
            }
        },

        hashRoute(hashRoute, oldHashRoute) {
            hashRoute !== oldHashRoute && this.handleHashRoute(hashRoute, this.user);
        }
    },

    computed: {
        mainSidebarState() {
            return this.$store.state.sidebar.main;
        },

        userSidebarState() {
            return this.$store.state.sidebar.user;
        },

        isDeviceMobile() {
            return this.$store.state.isDeviceMobile;
        },

        isLobby() {
            return this.$route.name === routesNames.GAME_LOBBY_SHORT || this.$route.name === routesNames.GAME_LOBBY;
        },

        isMobileLobby() {
            return this.isLobby && this.isDeviceMobile;
        },

        user() {
            return this.$store.state.user;
        },

        locale() {
            return this.$store.state.locale;
        },

        launchedGames() {
            return this.$store.state.launchedGames;
        },

        signupEnabled() {
            return appConfig.features.signup;
        },

        hashRoute() {
            return this.$route.hash;
        },

        isFortuneWheelRoute() {
            return this.hashRoute === hashRoutes.fortuneWheel;
        },
    },

    methods: {
        resize(){
            this.defineScreenResolution();
            this.initChat(false, true);
        },

        orientationchange(){
            setTimeout(() => {
                this.defineOrientation();
                this.initChat(false, true);
            }, 10);
        },

        async setUser() {
            let {default:setLoggedInUser} = await import('./util/user/setLoggedInUser');

            setLoggedInUser(this);
        },

        initChat(withDelay = false, reinitialize = false) {
            setTimeout(() => require('./chat/index').initChat(this, reinitialize), withDelay ? 4000 : 0);
        },

        fetchFortuneWheels(reload = false) {
            this.$store.dispatch('FETCH_FORTUNE_WHEELS', this.createApiData({
                reload,
            }))
                .then(({customer}) => {
                    customer && setGuestToken(customer.guest_token, this.$store, this.$cookies);

                    if (this.isFortuneWheelRoute) {
                        if (this.$store.getters.getAvailableFortuneWheelSpinsCount()) {
                            this.$store.commit('OPEN_FORTUNE_WHEEL');
                        } else {
                            this.makeErrorToast(this.$t('front.fortune_wheel.errors.no_available_spins'))
                        }
                    }
                })
        },

        defineScreenResolution() {
            this.$store.commit('SET_IS_DEVICE_MOBILE', isDeviceMobile(window));
        },

        defineOrientation() {
            this.$store.commit('SET_DEVICE_ORIENTATION', getDeviceOrientation(window));
        },

        switchMainSidebar() {
            if (this.$store.state.sidebar.main) {
                this.$store.commit('CLOSE_MAIN_SIDEBAR');
            } else {
                this.$store.commit('OPEN_MAIN_SIDEBAR');
            }
        },

        openLoginDialog() {
            this.$store.commit('CLOSE_FORGOT_PASSWORD_DIALOG');
            this.$store.commit('OPEN_LOGIN_DIALOG');
        },

        openForgotDialog() {
            this.$store.commit('CLOSE_LOGIN_DIALOG');
            this.$store.commit('OPEN_FORGOT_PASSWORD_DIALOG');
        },

        async userSessionInvalid() {
            if (this.user && !this.$store.state.isUserLoggingOut) {
                this.$store.commit('SET_IS_USER_LOGGING_OUT', true);

                this.makeErrorToast(this.$t('front.api.invalid_user_session'));

                let {default:logOutUser} = await import('./util/user/logOutUser');
                logOutUser(this).then(() => this.$store.commit('SET_IS_USER_LOGGING_OUT', false));
            }
        },

        resetFortuneWheel(){
            this.$store.commit('SET_FORTUNE_WHEELS', []);
            this.$store.commit('SET_FORTUNE_WHEEL_CAPTCHA_PASSED', false);

            this.fetchFortuneWheels(true);
        },

        loadUserTranslationMessages(){
            this.$store.dispatch('FETCH_TRANSLATION_MESSAGES', this.createApiData({
                locales: this.$store.state.fallbackLocales.concat([this.$store.state.locale]),
                scopes: ['FRONT_BASE', 'FRONT_USER', 'FRONT_PAYMENT'],
                appendMode: true,
            }))
                .then(translationMessages => Object.keys(translationMessages).forEach(locale => {
                    this.$i18n.setLocaleMessage(locale, translationMessages[locale])
                }));
        },
    },
}
</script>

<style lang="sass">
@import "sass/AppContent"
</style>